import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import {
  CExpandableFaqs,
  CExpandableFaqsBlock,
  CExpandableFaqsFragments,
  CExpandableFaqsEntry,
  CMastheadBlock,
  CMastheadFragments,
  CMinorImageMasthead,
  CMinorImageMastheadBlock,
  CMinorImageMastheadFragments,
  CSeomatic,
  CWysiwyg,
  CWysiwygFragments,
  SeomaticData,
  CFindADoctor,
  CCenteredCopy,
  CMasthead,
  CCustomComponentsLp,
  CCopyBlockBlock,
  CCustomComponentsBlogBlock,
  CCustomComponentsLpFragments,
  CImageCopySlides,
  CImageCopySlidesBlock,
  CImageCopySlidesFragments,
  CImageCopyBlocks,
  CImageCopyBlocksBlock,
  CImageCopyBlocksFragments,
  CBackgroundImageList,
  CBackgroundImageListBlock,
  CBackgroundImageListFragments,
  CProcedureSteps,
  CProcedureStepsBlock,
  CProcedureStepsFragments,
  CHero,
  CHeroBlock,
  CHeroFragment,
  CCtaContact,
  CCtaContactBlock,
  CCtaContactFragment,
  CCtaColumns,
  CCtaColumnsBlock,
  CCtaColumnsFragment,
  CTestimonialsSlider,
  CStats,
  CFaqSlider,
  CBackgroundImageCopy,
  CBackgroundImageCopyBlock,
  CBackgroundImageCopyFragments,
  CCtaBox,
  CCtaBoxBlock,
  CCtaBoxFragments,
  CAboutVideo,
  CComparisonTable,
  CCounter,
} from 'ui'
import { GetServerSidePropsContext } from 'next'
import { CraftGlobals } from 'tsconfig/craft-types'
import { gql } from '@urql/core'
import { getSiteInfo, serverSideValidateWww, useSiteInfo } from 'shared'
import {
  CFeaturedItems,
  CFeaturedItemsBlock,
  CFeaturedItemsFragment,
} from 'ui/components/organisms/c-featured-items'
import fragments from '../helpers/gql-fragments'
import { defaultClient } from '../graphql/clients'
import { getI18nResolvedUrl } from '../helpers/locale-tools'
import DefaultLayout from '../layouts/default'
import getGlobals from '../graphql/get-globals'
import { setCacheHeaders } from '../helpers/cache-config'

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const isWww = serverSideValidateWww({ context })

  if (isWww) {
    return {
      notFound: true,
    }
  }

  const siteInfo = getSiteInfo(context.locale ?? '')
  const global = await getGlobals(siteInfo.handle)
  const uri = getI18nResolvedUrl(context.req.url ?? '')

  const result = await defaultClient(siteInfo.handle)
    .query(
      gql`
        query($uri: [String]!, $site: [String]!, $section: [String]!, $type: [String]!) {
          entry(uri: $uri, site: $site, section: $section, type: $type) {
            ${fragments.seomatic}
            uri
            sectionHandle
            typeHandle
            title
            ... on generalPages_landingPage_Entry {
              customComponentsLandingPage {
                ...customComponentsLandingPage
              }
              masthead {
                ...masthead
              }
              featuredItems {
                ...featuredItems
              }
            }
            ... on generalPages_legalPage_Entry {
              expandableFaqs {
                ...expandableFaqs
              }
              minorImageMasthead {
                ...minorImageMasthead
              }
              wysiwyg {
                ...wysiwyg
              }
            }
            ... on generalPages_lens_Entry {
              masthead {
                ...masthead
              }
              featuredItems {
                ...featuredItems
              }
              backgroundImageCopy {
                ...backgroundImageCopy
              }
              backgroundImageCopy2 {
                ...backgroundImageCopy2
              }
              imageCopyBlocks {
                ...imageCopyBlocks
              }
              ctaBox {
                ...ctaBox
              }
            }
            ... on generalPages_procedure_Entry {
              masthead {
                ...masthead
              }
              imageCopySlides {
                ...imageCopySlides
              }
              imageCopyBlocks {
                ...imageCopyBlocks
              }
              backgroundImageList {
                ...backgroundImageList
              }
              procedureSteps {
                ...procedureSteps
              }
              hero {
                ...hero
              }
              ctaContact {
                ...ctaContact
              }
              ctaColumns {
                ...ctaColumns
              }
              expandableFaqs {
                ...expandableFaqs
              }
            }
          }
          faqs: entries(section: "faq", site: $site) {
            ...expandableFaqsFaqEntry
          }
        }
        ${CMastheadFragments.field}
        ${CExpandableFaqsFragments.field}
        ${CFeaturedItemsFragment}
        ${CMinorImageMastheadFragments.field}
        ${CCustomComponentsLpFragments.field}
        ${CWysiwygFragments.field}
        ${CExpandableFaqsFragments.faqEntry}
        ${CHeroFragment}
        ${CImageCopySlidesFragments.field}
        ${CImageCopyBlocksFragments.field}
        ${CBackgroundImageListFragments.field}
        ${CProcedureStepsFragments.field}
        ${CCtaContactFragment}
        ${CCtaColumnsFragment}
        ${CBackgroundImageCopyFragments.field}
        ${CBackgroundImageCopyFragments.field2}
        ${CCtaBoxFragments.field}
      `,
      {
        uri,
        site: siteInfo.handle,
        section: ['generalPages'],
        type: ['landingPage', 'legalPage', 'lens', 'procedure'],
      }
    )
    .toPromise()

  const data = result.data

  if (!data?.entry) {
    return {
      notFound: true,
    }
  }

  setCacheHeaders(context.res)

  return {
    props: {
      ...(await serverSideTranslations(context.locale ?? '', ['common'])),
      entry: data.entry,
      global,
      faqs: data.faqs,
    },
  }
}

type Props = {
  entry: {
    typeHandle: string
    seomatic: SeomaticData
    masthead: Array<CMastheadBlock>
    expandableFaqs: Array<CExpandableFaqsBlock>
    minorImageMasthead: Array<CMinorImageMastheadBlock>
    featuredItems: Array<CFeaturedItemsBlock>
    customComponentsLandingPage: Array<CCustomComponentsBlogBlock>
    wysiwyg: Array<CCopyBlockBlock>
    imageCopySlides: Array<CImageCopySlidesBlock>
    imageCopyBlocks: Array<CImageCopyBlocksBlock>
    backgroundImageList: Array<CBackgroundImageListBlock>
    procedureSteps: Array<CProcedureStepsBlock>
    hero: Array<CHeroBlock>
    ctaContact: Array<CCtaContactBlock>
    ctaColumns: Array<CCtaColumnsBlock>
    backgroundImageCopy: Array<CBackgroundImageCopyBlock>
    backgroundImageCopy2: Array<CBackgroundImageCopyBlock>
    ctaBox: Array<CCtaBoxBlock>
  }
  global: CraftGlobals
  faqs: Array<CExpandableFaqsEntry>
}

export default function CustomPage({ entry, global, faqs }: Props) {
  const siteInfo = useSiteInfo()

  const safeFeaturedItems = Array.isArray(entry?.featuredItems)
    ? entry.featuredItems
    : []

  return (
    <DefaultLayout global={global}>
      <CSeomatic data={entry.seomatic} />

      {entry.typeHandle === 'landingPage' && (
        <>
          <CMasthead field={entry.masthead} />
          <CCustomComponentsLp
            field={Array.isArray(entry.customComponentsLandingPage) ? entry.customComponentsLandingPage : []}
            global={global}
          />
          {siteInfo.isUK && safeFeaturedItems.length > 0 && (
            <CFeaturedItems field={safeFeaturedItems} />
          )}

          <CCenteredCopy field={Array.isArray(global?.centeredCopy) ? global.centeredCopy : []} />
        </>
      )}

      {entry.typeHandle === 'legalPage' && (
        <>
          <CMinorImageMasthead field={entry.minorImageMasthead} hasSpacer={false} />
          <CWysiwyg field={entry.wysiwyg} />
          <CExpandableFaqs faqs={entry.expandableFaqs} showSearchBar={true} />
          <CFindADoctor field={global.findADoctor} />
          <CCenteredCopy field={Array.isArray(global?.centeredCopy) ? global.centeredCopy : []} />
        </>
      )}

      {entry.typeHandle === 'lens' && (
        <>
          <CMasthead field={entry.masthead} />
          {siteInfo.isUK && safeFeaturedItems.length > 0 && (
            <CFeaturedItems field={safeFeaturedItems} />
          )}
          <CBackgroundImageCopy field={entry.backgroundImageCopy} />
          <CBackgroundImageCopy field={entry.backgroundImageCopy2} />
          <CImageCopyBlocks field={entry.imageCopyBlocks} />
          <CAboutVideo field={global?.aboutVideo} />
          <CComparisonTable field={global?.comparisonTable} />
          <CCounter field={global?.counter} />
          <CTestimonialsSlider field={global?.testimonialsSlider} />
          <CCtaBox field={entry.ctaBox} />
          <CFindADoctor field={global?.findADoctor} />
          <CCenteredCopy field={Array.isArray(global?.centeredCopy) ? global.centeredCopy : []} />
        </>
      )}

      {entry.typeHandle === 'procedure' && (
        <>
          {siteInfo.isUS ? (
            <>
              <CHero field={entry.hero} isProcedure={true} />
              <CProcedureSteps field={entry.procedureSteps} />
              <CExpandableFaqs field={entry.expandableFaqs} />
              <CTestimonialsSlider field={global.testimonialsSlider} />
              <CCtaContact field={entry.ctaContact} />
              <CCtaColumns field={entry.ctaColumns} />
              <CFaqSlider field={global.faqVideoSlider} isProcedure={true} />
              <CCenteredCopy field={Array.isArray(global?.centeredCopy) ? global.centeredCopy : []} />
            </>
          ) : (
            <>
              <CMasthead field={entry.masthead} />
              <CImageCopySlides
                field={entry.imageCopySlides}
                lensBranding={siteInfo.branded}
              />
              <CImageCopyBlocks field={entry.imageCopyBlocks} />
              <CBackgroundImageList field={entry.backgroundImageList} />
              <CTestimonialsSlider field={global.testimonialsSlider} />
              <CStats field={global.stats} />
              <CFaqSlider field={global.faqVideoSlider} />
              <CFindADoctor field={global.findADoctor} />
              <CCenteredCopy field={Array.isArray(global?.centeredCopy) ? global.centeredCopy : []} />
            </>
          )}
        </>
      )}
    </DefaultLayout>
  )
}